import { isValidElement, memo, useCallback, useEffect, useRef, useState } from 'react';
import type { Cell, Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { Box, styled, TableCell as MuiTableCell, Tooltip } from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';

import type { DataSchema, Schema } from 'types';
import { getUiDefinitionContainerStyles, getUiDefinitionTextStyles } from '../../../utils/styles.ts';
import type { HeliosCellData } from '../../../types/helios/agendaListing';
import { useIsOverflowing } from '../../../utils/hooks';
import { safelyStringifyToJSON } from 'utils/common';
import { getCommonPinningStyles } from './tableFunctions';
import { AgendaCellContent } from './AgendaCellContent';
import type { TableColumnAggregationState } from 'utils/table.ts';
import type { ColumnOverflowMode } from 'types/formItem';

const StyledTableCell = styled(MuiTableCell)(() => ({
  paddingTop: 0,
  paddingBottom: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
}));

interface TableCellProps<T extends keyof P, P extends DataSchema> {
  cell: Cell<Schema<T, P>, unknown>;
  entireRowClicked?: (row: Schema<T, P>) => void;
  row: Row<Schema<T, P>>;
  isInnerTableOpen?: boolean;
  clickableCellsKeys: (keyof P)[] | 'all';
  disabled?: boolean;
  /**
   * This prop is needed to trigger a re-render when column aggregation changes.
   * It is not used in the function body, but its presence in the props object ensures that
   * React.memo detects a change and refreshes grouped/aggregated cell output.
   */
  columnAggregation?: TableColumnAggregationState;
}

type ColumnMeta<P extends DataSchema> = {
  uiDefinition: P;
  columnOverflowMode?: ColumnOverflowMode;
};

/**
 * Usually string or undefined,
 *
 * in Flow it can be an array of objects with "value" key, e.g.
 *
 * [
 *   {"id": "18c04147-5356-430c-9c54-62a0dbc59413", "value": "ČSA J3"},
 *   {"id": "4c413e76-d896-4a8d-951e-fd3ebc12b1ab", "value": "Paskov"},
 *   {"id": "61d3adad-b577-435f-b765-c87f3e72c709", "value": "ČSA J2"}
 * ]
 *
 * or array of strings, e.g.
 *
 * ["f82882ee-1827-4fae-b46d-d58c51141416", "4d08036a-f374-4cc3-b330-4219f198d586"]
 *
 * Both array of objects and arrays of strings are rendered by custom cellRenderer.
 */
type PossibleTypesOfCellValue = string | undefined | Array<{ id: string; value: string }> | string[];

/**
 * Type guard to check if the value is an array of objects with "value" key.
 */
const isArrayWithValueKey = (value: PossibleTypesOfCellValue | unknown): value is Array<{ id: string; value: string }> => {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'object' && item !== null && 'value' in item && typeof item.value === 'string')
  );
};

const arrayWithValueToString = (value: Array<{ id: string; value: string }>): string => {
  return value.map((item) => item.value).join('; ');
};

const isArrayWithFullNameKey = (value: PossibleTypesOfCellValue | unknown): value is Array<{ id: string; fullName: string }> => {
  return (
    Array.isArray(value) &&
    value.every((item) => typeof item === 'object' && item !== null && 'fullName' in item && typeof item.fullName === 'string')
  );
};

const arrayWithFullNameToString = (value: Array<{ id: string; fullName: string }>): string => {
  return value.map((item) => item.fullName).join('; ');
};

const getTooltipSafeValue = (value: PossibleTypesOfCellValue | unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return value.toString();
  if (isArrayWithValueKey(value)) {
    return arrayWithValueToString(value);
  }
  if (isArrayWithFullNameKey(value)) {
    return arrayWithFullNameToString(value);
  }
  if (Array.isArray(value) && value.every((item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')) {
    return value.join('; ');
  }
  return safelyStringifyToJSON(value);
};

const containsReactElement = (value: unknown): boolean => {
  return isValidElement(value) || (Array.isArray(value) && value.some((item) => isValidElement(item)));
};

const getRenderedTextContent = (element: HTMLElement | null): string | undefined => {
  const text = element?.textContent?.replace(/\s+/g, ' ').trim();
  return text ? text : undefined;
};

const TableCellBase = <T extends keyof P, P extends DataSchema>({ cell, entireRowClicked, row }: TableCellProps<T, P>) => {
  const value = cell.getValue() as PossibleTypesOfCellValue;
  const cellData = cell.getValue() as HeliosCellData;
  const uiDefinition = cellData?.uiDefinition ?? (cell.column.columnDef.meta as ColumnMeta<P>)?.uiDefinition;
  const boxStyles = getUiDefinitionContainerStyles(uiDefinition);
  const textStyles = getUiDefinitionTextStyles(uiDefinition);
  const overflowMode = (cell.column.columnDef.meta as ColumnMeta<P>)?.columnOverflowMode;

  const renderCellContent = useCallback(() => {
    const cellRenderer =
      cell.getIsAggregated() && cell.column.columnDef.aggregatedCell ? cell.column.columnDef.aggregatedCell : cell.column.columnDef.cell;

    if (typeof cellRenderer === 'function') {
      return flexRender(cellRenderer, cell.getContext());
    }

    if (!value) return null;

    if (isArrayWithValueKey(value)) {
      return arrayWithValueToString(value);
    }
    return value;
  }, [cell, value]);

  const isOverflowingRef = useRef<HTMLElement | null>(null);
  const { isOverflowing } = useIsOverflowing(isOverflowingRef, renderCellContent, 2);
  const [tooltipTitle, setTooltipTitle] = useState<string | undefined>();

  useEffect(() => {
    setTooltipTitle(
      isOverflowing ? (containsReactElement(value) ? getRenderedTextContent(isOverflowingRef.current) : getTooltipSafeValue(value)) : undefined
    );
  }, [isOverflowing, value]);

  const isGrouped = cell.getIsGrouped();
  const isAggregated = cell.getIsAggregated();
  const isPlaceholder = cell.getIsPlaceholder();
  const isRowExpanded = row.getIsExpanded();

  const handleRowExpand = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    row.getToggleExpandedHandler()();
  };

  const GroupedContent = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', cursor: row.getCanExpand() ? 'pointer' : 'normal' }} onClick={handleRowExpand}>
      {isRowExpanded ? <KeyboardArrowDown fontSize="small" sx={{ mr: 0.5 }} /> : <KeyboardArrowRight fontSize="small" sx={{ mr: 0.5 }} />}
      {renderCellContent()} <p style={{ marginLeft: '6px' }}>({row.subRows.length})</p>
    </Box>
  );

  const renderCell = () => {
    switch (true) {
      case isGrouped:
        return <GroupedContent />;
      case isAggregated:
        return renderCellContent();
      case isPlaceholder:
        return null;
      default:
        return renderCellContent();
    }
  };

  return (
    <StyledTableCell
      data-auto-column={(cell.column.columnDef.meta as { automaticSizing?: boolean })?.automaticSizing ? cell.column.id : undefined}
      data-testid={`table-cell-${cell.row.index + 1}-${cell.column.id}`}
      key={cell.id}
      sx={{
        ...getCommonPinningStyles(cell.column, { id: cell.row.id }),
        whiteSpace: overflowMode === 'Wrap' ? 'normal' : 'nowrap',
      }}
      onClick={(e) => {
        e.stopPropagation();
        entireRowClicked?.(row.original);
      }}>
      {overflowMode ? (
        <AgendaCellContent mode={overflowMode} sx={{ px: '8px', ...boxStyles, ...textStyles }}>
          {renderCell()}
        </AgendaCellContent>
      ) : (
        <Tooltip title={tooltipTitle} arrow>
          <Box
            ref={isOverflowingRef}
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: isGrouped ? 'flex' : 'block',
              alignItems: 'center',
              px: '8px',
              ...boxStyles,
              ...textStyles,
            }}>
            {renderCell()}
          </Box>
        </Tooltip>
      )}
    </StyledTableCell>
  );
};

export const TableCell = memo(TableCellBase) as typeof TableCellBase;
